// src/utils/competitionStatus.ts
//
// Pure helpers that derive a competition's live status from the fields the
// admin already sets — the manual `status` flag plus the date deadlines. No
// new Firestore fields are required. Used to render clear status chips on the
// public competition page and (re)usable anywhere a "is this open right now?"
// decision is needed.
//
// Design note: registration is considered open only when the admin flag says
// "open" AND the registration deadline has not passed. The DATE is therefore
// authoritative — it closes the "admin forgot to toggle the status" gap.

function toDate(v: any): Date | null {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export interface CompetitionStatusFlags {
  /** New athletes can be registered right now. */
  registrationOpen: boolean;
  /** Coaches may still change weight right now. */
  weightChangesOpen: boolean;
  /** The competition start date has arrived. */
  started: boolean;
  /** The competition end date has passed. */
  finished: boolean;
}

/**
 * Registration is open only when BOTH the admin flag is "open" AND the
 * registration deadline has not passed. When no deadline date exists we fall
 * back to the flag alone.
 */
export function isRegistrationOpen(comp: any, now: Date = new Date()): boolean {
  if (!comp) return false;
  if (comp.status && comp.status !== "open") return false;
  const deadline = toDate(comp.registrationDeadline);
  if (!deadline) return comp.status === "open";
  return now.getTime() < deadline.getTime();
}

/**
 * Coaches can change weight until weightChangeDeadline, falling back to
 * startDate when the dedicated field is absent (mirrors
 * getWeightChangeDeadline() in firebase/registrations.ts and the Firestore rule).
 */
export function isWeightChangeOpen(comp: any, now: Date = new Date()): boolean {
  if (!comp) return false;
  const cutoff = toDate(comp.weightChangeDeadline) ?? toDate(comp.startDate);
  if (!cutoff) return false;
  return now.getTime() < cutoff.getTime();
}

export function getCompetitionStatus(
  comp: any,
  now: Date = new Date()
): CompetitionStatusFlags {
  const start = toDate(comp?.startDate);
  const end = toDate(comp?.endDate);
  return {
    registrationOpen: isRegistrationOpen(comp, now),
    weightChangesOpen: isWeightChangeOpen(comp, now),
    started: !!start && now.getTime() >= start.getTime(),
    finished: !!end && now.getTime() > end.getTime(),
  };
}
